import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, useInput, useStdout, useWindowSize } from "ink";
import StatusBar from "./StatusBar";
import HelpModal from "./HelpModal";
import BranchCreationWizard from "./BranchCreationWizard";
import OpenEditorWizard from "./OpenEditorWizard";
import WorktreeStatusView from "./WorktreeStatusView";
import ForceCleanModal from "./ForceCleanModal";
import LogPanel from "./LogPanel";
import { MOUSE_TRACKING_DISABLE, MOUSE_TRACKING_ENABLE, isMouseSequence } from "../utils/mouse";
import type { AppEventEmitter } from "../utils/app-events";
import type { AppSyncProgress } from "../utils/app-events";
import type {
  HookContext,
  WorktreeStatusEntry,
  DivergedDirectoryInfo,
  RepositoryListEntry,
  RepositoryDiskUsage,
  ForceCleanRepositoryPreview,
  ForceCleanRepositoryResult,
} from "../types";

export type { HookContext, WorktreeStatusEntry };

export interface AppProps {
  events: AppEventEmitter;
  repositoryCount: number;
  cronSchedule?: string;
  onManualSync: () => void;
  onReload: () => void;
  onQuit: () => Promise<void>;
  maxProgressLines?: number;
  getRepositoryList: () => RepositoryListEntry[];
  getRepositoryDiskUsage?: (index: number) => Promise<RepositoryDiskUsage>;
  getBranchesForRepo: (index: number) => Promise<string[]>;
  getDefaultBranchForRepo: (index: number) => string;
  fetchForRepo?: (index: number) => Promise<void>;
  createAndPushBranch: (
    repoIndex: number,
    baseBranch: string,
    branchName: string,
  ) => Promise<{ success: boolean; finalName: string; error?: string }>;
  getWorktreesForRepo: (index: number) => Promise<Array<{ path: string; branch: string }>>;
  openEditorInWorktree: (worktreePath: string) => { success: boolean; error?: string };
  openTerminalInWorktree: (
    repoIndex: number,
    worktreePath: string,
    branchName: string,
  ) => { success: boolean; error?: string };
  copyBranchFiles?: (repoIndex: number, baseBranch: string, targetBranch: string) => Promise<void>;
  createWorktreeForBranch: (repoIndex: number, branchName: string) => Promise<void>;
  executeOnBranchCreatedHooks?: (repoIndex: number, context: HookContext) => void;
  getWorktreeStatusForRepo?: (index: number) => Promise<WorktreeStatusEntry[]>;
  getDivergedDirectoriesForRepo?: (index: number) => Promise<DivergedDirectoryInfo[]>;
  deleteDivergedDirectory?: (repoIndex: number, name: string) => Promise<void>;
  getForceCleanPreview?: () => Promise<ForceCleanRepositoryPreview[]>;
  forceClean?: (repoIndexes: number[]) => Promise<ForceCleanRepositoryResult[]>;
}

export interface LogEntry {
  id: string;
  message: string;
  level: "info" | "warn" | "error";
  timestamp: Date;
}

const MAX_LOG_ENTRIES = 5000;

const App: React.FC<AppProps> = ({
  events,
  repositoryCount,
  cronSchedule,
  onManualSync,
  onReload,
  onQuit,
  maxProgressLines = 2,
  getRepositoryList,
  getRepositoryDiskUsage,
  getBranchesForRepo,
  getDefaultBranchForRepo,
  fetchForRepo,
  createAndPushBranch,
  getWorktreesForRepo,
  openEditorInWorktree,
  openTerminalInWorktree,
  copyBranchFiles,
  createWorktreeForBranch,
  executeOnBranchCreatedHooks,
  getWorktreeStatusForRepo,
  getDivergedDirectoriesForRepo,
  deleteDivergedDirectory,
  getForceCleanPreview,
  forceClean,
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [showBranchWizard, setShowBranchWizard] = useState(false);
  const [showOpenEditorWizard, setShowOpenEditorWizard] = useState(false);
  const [showWorktreeStatus, setShowWorktreeStatus] = useState(false);
  const [showForceClean, setShowForceClean] = useState(false);
  const [status, setStatus] = useState<"idle" | "syncing">("idle");
  // Interactive operations (branch/worktree creation) run independently of sync and
  // queue behind it. Tracked separately so they don't drive the sync `status` spinner.
  const [activeOps, setActiveOps] = useState<Array<{ id: number; label: string }>>([]);
  const opIdRef = useRef(0);
  const [syncProgressEntries, setSyncProgressEntries] = useState<AppSyncProgress[]>([]);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [diskSpaceUsed, setDiskSpaceUsed] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [repoCount, setRepoCount] = useState(repositoryCount);
  const [schedule, setSchedule] = useState(cronSchedule);

  const { rows } = useWindowSize();
  const { write } = useStdout();

  // Terminals only report the wheel while tracking is on, and it must be turned
  // back off on exit or the shell inherits a terminal that swallows clicks.
  useEffect(() => {
    write(MOUSE_TRACKING_ENABLE);
    return () => {
      write(MOUSE_TRACKING_DISABLE);
    };
  }, [write]);

  const addLog = useCallback((message: string, level: LogEntry["level"] = "info") => {
    setLogs((prev) => {
      const newLogs = [
        ...prev,
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          message,
          level,
          timestamp: new Date(),
        },
      ];
      if (newLogs.length > MAX_LOG_ENTRIES) {
        return newLogs.slice(-MAX_LOG_ENTRIES);
      }
      return newLogs;
    });
  }, []);

  const addLogRef = useRef(addLog);
  addLogRef.current = addLog;

  useInput((input, key) => {
    // Mouse reports reach every useInput; only the scrollable panel acts on
    // them, and no shortcut here should fire off a stray click.
    if (isMouseSequence(input)) return;

    if (showHelp) {
      if (input === "?" || input === "h" || key.escape) {
        setShowHelp(false);
      }
      return;
    }

    if (showBranchWizard || showOpenEditorWizard || showWorktreeStatus || showForceClean) {
      return;
    }

    if (input === "q") {
      onQuit().catch((err) => console.error("Quit failed:", err));
    } else if (input === "?" || input === "h") {
      setShowHelp(true);
    } else if (input === "c") {
      setShowBranchWizard(true);
    } else if (input === "o") {
      setShowOpenEditorWizard(true);
    } else if (input === "w" && getWorktreeStatusForRepo) {
      setShowWorktreeStatus(true);
    } else if (input === "x" && getForceCleanPreview && forceClean && status !== "syncing") {
      setShowForceClean(true);
    } else if (input === "s" && status !== "syncing") {
      setStatus("syncing");
      (async () => {
        try {
          await onManualSync();
        } catch (error) {
          console.error("Manual sync failed:", error);
          setStatus("idle");
        }
      })().catch((err) => console.error("Manual sync unhandled error:", err));
    } else if (input === "r" && status !== "syncing") {
      setStatus("syncing");
      (async () => {
        try {
          await onReload();
        } catch (error) {
          console.error("Reload failed:", error);
          setStatus("idle");
        }
      })().catch((err) => console.error("Reload unhandled error:", err));
    }
  });

  useEffect(() => {
    const unsubscribers = [
      events.on("updateLastSyncTime", () => {
        setLastSyncTime(new Date());
        setStatus("idle");
        setSyncProgressEntries([]);
      }),
      events.on("setStatus", (newStatus: "idle" | "syncing") => {
        setStatus(newStatus);
        if (newStatus === "idle") {
          setSyncProgressEntries([]);
        }
      }),
      events.on("setSyncProgress", (progress: AppSyncProgress | null) => {
        if (progress === null) {
          setSyncProgressEntries([]);
          return;
        }
        setSyncProgressEntries((prev) => {
          if (progress.completed) {
            return prev.filter((entry) => entry.repo !== progress.repo);
          }

          const existingIndex = prev.findIndex((entry) => entry.repo === progress.repo);
          if (existingIndex === -1) {
            return [...prev, progress];
          }

          return prev.map((entry, index) => (index === existingIndex ? progress : entry));
        });
      }),
      events.on("setDiskSpace", (diskSpace: string) => {
        setDiskSpaceUsed(diskSpace);
      }),
      events.on("addLog", ({ message, level }: { message: string; level: "info" | "warn" | "error" }) => {
        addLogRef.current(message, level);
      }),
      events.on("updateRepositoryCount", (count: number) => {
        setRepoCount(count);
      }),
      events.on("updateCronSchedule", (newSchedule: string | undefined) => {
        setSchedule(newSchedule);
      }),
    ];

    events.emit("uiReady");

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const progressLineCount = status === "syncing" ? Math.max(1, maxProgressLines) : 0;
  const statusBarHeight = 5 + progressLineCount + activeOps.length;
  const terminalRows = rows ?? 24;
  const logPanelHeight = Math.max(5, terminalRows - statusBarHeight);
  const showModal = showHelp || showBranchWizard || showOpenEditorWizard || showWorktreeStatus || showForceClean;

  return (
    <Box flexDirection="column" minHeight={terminalRows}>
      {!showModal && <LogPanel logs={logs} height={logPanelHeight} isActive={!showModal} />}

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {showBranchWizard && (
        <BranchCreationWizard
          repositories={getRepositoryList()}
          getBranchesForRepo={getBranchesForRepo}
          getDefaultBranchForRepo={getDefaultBranchForRepo}
          fetchForRepo={fetchForRepo}
          createAndPushBranch={createAndPushBranch}
          onClose={() => setShowBranchWizard(false)}
          onBranchCreated={(context) => {
            const opId = ++opIdRef.current;
            setActiveOps((prev) => [...prev, { id: opId, label: `Creating worktree ${context.newBranch}` }]);
            (async () => {
              try {
                await createWorktreeForBranch(context.repoIndex, context.newBranch);
                if (copyBranchFiles) {
                  await copyBranchFiles(context.repoIndex, context.baseBranch, context.newBranch);
                }

                if (executeOnBranchCreatedHooks) {
                  const worktrees = await getWorktreesForRepo(context.repoIndex);
                  const worktree = worktrees.find((w) => w.branch === context.newBranch);
                  if (worktree) {
                    const repos = getRepositoryList();
                    const repo = repos.find((r) => r.index === context.repoIndex);
                    executeOnBranchCreatedHooks(context.repoIndex, {
                      branchName: context.newBranch,
                      worktreePath: worktree.path,
                      repoName: repo?.name || `repo-${context.repoIndex}`,
                      baseBranch: context.baseBranch,
                      repoUrl: repo?.repoUrl || "",
                    });
                  }
                }
              } catch (error) {
                const errorMsg = error instanceof Error ? error.message : String(error);
                events.emit("addLog", {
                  message: `Failed to create worktree: ${errorMsg}`,
                  level: "error",
                });
              } finally {
                setActiveOps((prev) => prev.filter((op) => op.id !== opId));
              }
            })().catch((err) => console.error("Branch creation unhandled error:", err));
          }}
          onComplete={() => {
            setShowBranchWizard(false);
          }}
        />
      )}

      {showOpenEditorWizard && (
        <OpenEditorWizard
          repositories={getRepositoryList()}
          getWorktreesForRepo={getWorktreesForRepo}
          openEditorInWorktree={openEditorInWorktree}
          openTerminalInWorktree={openTerminalInWorktree}
          onClose={() => setShowOpenEditorWizard(false)}
        />
      )}

      {showWorktreeStatus && getWorktreeStatusForRepo && (
        <WorktreeStatusView
          repositories={getRepositoryList()}
          getWorktreeStatusForRepo={getWorktreeStatusForRepo}
          getRepositoryDiskUsage={getRepositoryDiskUsage}
          getDivergedDirectoriesForRepo={getDivergedDirectoriesForRepo}
          deleteDivergedDirectory={deleteDivergedDirectory}
          onClose={() => setShowWorktreeStatus(false)}
        />
      )}

      {showForceClean && getForceCleanPreview && forceClean && (
        <ForceCleanModal
          getPreview={getForceCleanPreview}
          forceClean={forceClean}
          onClose={() => setShowForceClean(false)}
        />
      )}

      <StatusBar
        status={status}
        syncProgressEntries={syncProgressEntries}
        activeOps={activeOps.map((op) => op.label)}
        maxProgressLines={maxProgressLines}
        repositoryCount={repoCount}
        lastSyncTime={lastSyncTime}
        cronSchedule={schedule}
        diskSpaceUsed={diskSpaceUsed ?? undefined}
      />
    </Box>
  );
};

export default App;
